/**
 * Tiny fetch wrapper. Handles:
 *   - relative '/api/...' paths
 *   - JSON body marshalling
 *   - CSRF token (fetched lazily, cached, re-fetched on 403)
 *   - typed error throwing
 *
 * All state-changing methods (POST/PATCH/DELETE) automatically send the
 * x-csrf-token header. GETs do not.
 */

export type ApiError = {
  status: number;
  error: string;
  issues?: unknown;
};

class CsrfStore {
  private token: string | null = null;
  private header = 'x-csrf-token';
  private inflight: Promise<void> | null = null;

  async ensure(): Promise<void> {
    if (this.token) return;
    if (this.inflight) return this.inflight;
    this.inflight = (async () => {
      const r = await fetch('/api/auth/csrf', { credentials: 'include' });
      if (!r.ok) throw new Error('failed to fetch CSRF token');
      const body = (await r.json()) as { token: string; header: string };
      this.token = body.token;
      this.header = body.header;
    })();
    try {
      await this.inflight;
    } finally {
      this.inflight = null;
    }
  }

  invalidate(): void {
    this.token = null;
  }

  get headers(): Record<string, string> {
    return this.token ? { [this.header]: this.token } : {};
  }
}

const csrf = new CsrfStore();

type ApiInit = Omit<RequestInit, 'body' | 'method'> & {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
};

const isDev = import.meta.env.DEV;

function devLog(method: string, path: string, status: number, fromRetry = false) {
  if (!isDev) return;
  const label = fromRetry ? `${method} ${path} (retry)` : `${method} ${path}`;
  // eslint-disable-next-line no-console
  console.log(
    `%c[api] ${label} → ${status}`,
    status >= 400 ? 'color:#dc2626;font-weight:600' : 'color:#16a34a',
  );
}

async function request<T>(path: string, init: ApiInit = {}): Promise<T> {
  const method = init.method ?? 'GET';
  const mutates = method !== 'GET';

  if (mutates) await csrf.ensure();

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    ...(mutates ? csrf.headers : {}),
    ...((init.headers as Record<string, string>) ?? {}),
  };

  const res = await fetch(path, {
    ...init,
    method,
    credentials: 'include',
    headers,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  devLog(method, path, res.status);

  if (res.status === 403 && mutates) {
    // CSRF token might have rotated. Refresh and retry once.
    csrf.invalidate();
    await csrf.ensure();
    const retry = await fetch(path, {
      ...init,
      method,
      credentials: 'include',
      headers: { ...headers, ...csrf.headers },
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
    devLog(method, path, retry.status, true);
    if (!retry.ok) throw await asApiError(retry);
    return (await retry.json()) as T;
  }

  if (!res.ok) throw await asApiError(res);

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function asApiError(res: Response): Promise<ApiError> {
  let body: { error?: string; issues?: unknown } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* not json */
  }
  return {
    status: res.status,
    error: body.error ?? `http_${res.status}`,
    issues: body.issues,
  };
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
